import { ppoReducerKey } from './reducer';
import { createSelector } from 'reselect';
import { camelizeKeys } from 'humps';

const extractFromState = (state) => {
  return state[ppoReducerKey];
};

export const getStyleboardInfo = (state) => {
  return extractFromState(state).styleboardInfo;
};

export const getIsStyleboardInfoLoading = (state) => {
  return extractFromState(state).styleboardInfoLoading;
};

export const getShouldFetchStyleboardInfo = (state) => {
  return extractFromState(state).shouldFetchStyleboardInfo;
};

export const getCurrentStyleboardInfo = () => (dispatch, getState) => {
  return getStyleboardInfo(getState());
};

export const getSpace = (state) => {
  return extractFromState(state).styleboard;
};

export const getIsSpaceLoading = (state) => {
  return extractFromState(state).styleboardLoading;
};

export const getShouldFetchSpace = (state) => {
  return extractFromState(state).shouldFetchSpaceData;
};

export const getCurrentSpace = () => (dispatch, getState) => {
  return getSpace(getState());
};

export const getPhotoSubmitSuccess = (state) => {
  return extractFromState(state).photosSubmitSuccess;
};

export const getPhotoSubmitError = (state) => {
  return extractFromState(state).photosSubmitError;
};

export const getPhotoSubmitWarning = (state) => {
  return extractFromState(state).photosSubmitWarning;
};

export const getArePhotosUploading = (state) => {
  return extractFromState(state).photosUploading;
};

export const getPhotosUploadTotal = (state) => {
  return extractFromState(state).photosUploadTotal;
};

export const getPhotosUploadCurrent = (state) => {
  return extractFromState(state).photosUploadCurrent;
};

export const getPhotosAndMeasurements = (state) => {
  return extractFromState(state).photosAndMeasurementData;
};

export const getArePhotosAndMeasurementsLoading = (state) => {
  return extractFromState(state).photosAndMeasurementDataLoading;
};

export const getFloorPlan = createSelector(
  (state) => getPhotosAndMeasurements(state),
  (photosAndMeasurementData) => {
    return photosAndMeasurementData &&
    photosAndMeasurementData.photoList &&
    photosAndMeasurementData.photoList.find(photo => photo.pos === 7);
  });

export const getProductCategoriesData = (state) => {
  return extractFromState(state).productCategoriesData;
};

export const getMappedProductCategoriesData = (state) => {
  const { results } = getProductCategoriesData(state) || {};
  return results && results.reduce((acc, item) => {
    const hasItem = item && item.id;
    if (hasItem) {
      acc[item.id] = item;
    }
    return acc;
  }, {});
};

export const getIsProductCategoriesDataLoading = (state) => {
  return extractFromState(state).productCategoriesDataLoading;
};

export const getAboutYouData = (state) => {
  return extractFromState(state).aboutYouData;
};

export const getIsAboutYouDataLoading = (state) => {
  return extractFromState(state).aboutYouDataLoading;
};

export const getIsAboutYouDataSubmitting = (state) => {
  return extractFromState(state).aboutYouDataSubmitting;
};

export const getSpaceSubmitSuccess = (state) => {
  return extractFromState(state).styleboardSubmitSuccess;
};

export const getIsSpaceSubmitLoading = (state) => {
  return extractFromState(state).styleboardSubmitLoading;
};

export const getSpaceSubmitError = (state) => {
  return extractFromState(state).styleboardSubmitError;
};

export const getSpaceList = (state) => {
  return extractFromState(state).spaceList;
};

export const getLatestPurchasedSpace = createSelector(
  state => getSpaceList(state),
  (spaceList) => {
    return Object.values(spaceList).sort((a, b) => new Date(b.orderDate()) - new Date(a.orderDate()))[0];
  }
);

export const getSpaceListPage = (state) => {
  return extractFromState(state).spaceListPage;
};

export const getIsSpaceListLoading = (state) => {
  return extractFromState(state).spaceListLoading;
};

export const getSpaceUserId = (state) => {
  return extractFromState(state).spaceUserId;
};

export const getIsSpaceUserIdLoading = (state) => {
  return extractFromState(state).spaceUserIdLoading;
};

export const getIsStyleMatchQuizComplete = (state) => {
  return extractFromState(state).styleMatchQuizComplete;
};

export const getIsStyleMatchQuizCompleteLoading = (state) => {
  return extractFromState(state).styleMatchQuizCompleteLoading;
};

export const getIsRoomCaptureCompleted = (state) => {
  return getStyleboardInfo(state).roomCaptureCompleted;
};

export const getSpaceV2 = (state) => {
  return extractFromState(state).styleboardV2;
};

export const getLegacyQuizResult = (state) => {
  return extractFromState(state).legacyStyleQuizResult;
};

export const getIsLegacyQuizResultLoading = (state) => {
  return extractFromState(state).legacyStyleQuizResultLoading;
};

export const getPPOMappedState = (state) => {
  const ppoData = { ...extractFromState(state).ppoData };

  if (!ppoData) return;

  const hasPPOState = ppoData && ppoData.state && ppoData.state.nodes && ppoData.state.nodes.length > 0;
  const ppoStateMap = hasPPOState && ppoData.state.nodes.reduce((obj, item) => {
    const hasQuestionNode = item && item.questions && item.questions.length > 0;
    obj[hasQuestionNode ? item.questions[0].name : item.name] = item;
    return obj;
  }, {});
  return ppoStateMap;
};

export const getShapeChoices = (state) => {
  const ppoStateMap = getPPOMappedState(state);
  if (!ppoStateMap) return {};

  const { questions } = ppoStateMap['spaceshape_questions'] || {};
  const { choices } = questions[0].value || [];
  const choicesMap = choices.reduce((obj, item) => {
    obj[item.name] = item;
    return obj;
  }, {});

  return choicesMap;
};

export const getIsShapeSelected = (state) => {
  const choices = getShapeChoices(state);
  return Object.keys(choices).some(key => choices[key].selected) || false;
};

export const getSelectedShape = (state) => {
  const choices = getShapeChoices(state);
  return Object.keys(choices).filter(key => choices[key].selected)[0] || '';
};

export const getMappedPPOState = (state) => {
  const { nodes } = extractFromState(state).ppoState.state || [];
  const hasNodes = nodes && nodes.length > 0;

  if (!hasNodes) return {};

  return nodes.reduce((acc, node) => {
    const hasName = node && node.name;
    if (hasName) {
      acc[node.name] = node;
    }
    return camelizeKeys(acc);
  }, {});
};
